import { useEffect, useState } from "react";
import useCountUp from "../hooks/useCountUp";
import useInView from "../hooks/useInView";
import { useLanguage } from "../context/LanguageContext";

const VALUE_RE = /^(\d+)(.*)$/;
const STAGGER_MS = 500;
// Mobile only: within one stacked stat block, the number, its rule, and its
// label reveal in this sequence one after another (rather than together),
// so each block reads as its own number → line → label beat before the
// next block's sequence begins.
const MOBILE_INTRA_STAGGER_MS = 150;

function useStaggeredReveal(active: boolean, delayMs: number): boolean {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (!active) return;
    const timer = setTimeout(() => setRevealed(true), delayMs);
    return () => clearTimeout(timer);
  }, [active, delayMs]);

  return revealed;
}

function AnimatedStat({
  value,
  active,
  delayMs,
}: {
  value: string;
  active: boolean;
  delayMs: number;
}) {
  const match = VALUE_RE.exec(value);
  const target = match ? Number(match[1]) : 0;
  const suffix = match ? match[2] : "";
  const revealed = useStaggeredReveal(active, delayMs);
  const count = useCountUp(target, revealed);

  return (
    <div
      className={`transition-all duration-700 ease-engineered ${
        revealed ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"
      }`}
    >
      <span className="font-display uppercase tracking-[0.3px] text-6xl font-normal text-ink sm:text-7xl md:text-8xl">
        {count}
        {suffix}
      </span>
    </div>
  );
}

function StatLabel({
  label,
  active,
  delayMs,
}: {
  label: string;
  active: boolean;
  delayMs: number;
}) {
  const revealed = useStaggeredReveal(active, delayMs);

  return (
    <div
      className={`font-sans text-xl font-semibold uppercase tracking-[-0.6px] text-ink transition-all duration-700 ease-engineered ${
        revealed ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"
      }`}
    >
      {label}
    </div>
  );
}

export default function StatsSection({ className = "" }: { className?: string }) {
  const { t } = useLanguage();
  const { ref, inView } = useInView<HTMLElement>(0.3);

  const stats = [
    { value: t("heroStat1Value"), label: t("heroStat1Label") },
    { value: t("heroStat2Value"), label: t("heroStat2Label") },
    { value: t("heroStat3Value"), label: t("heroStat3Label") },
  ];

  return (
    <section ref={ref} className={`bg-white pt-2 pb-16 md:pb-24 ${className}`}>
      <div className="container-industrial">
        <div className="mt-10 sm:mt-14 md:mt-16">
          {/* Mobile (<sm): each stat is one self-contained stacked unit —
              number, its own hairline, its own label directly beneath —
              so the number/label pairing survives single-column stacking.
              A single vertical line to the left grows top-to-bottom over
              the same total duration as the three blocks' reveal, so it
              reads as the line "unveiling" each stat as it passes. */}
          <div className="relative pl-6 sm:hidden">
            <div
              className="absolute inset-y-0 left-0 w-1.5 origin-top bg-accent transition-transform ease-engineered"
              style={{
                transform: `scaleY(${inView ? 1 : 0})`,
                transitionDuration: `${(stats.length - 1) * STAGGER_MS + MOBILE_INTRA_STAGGER_MS * 2 + 700}ms`,
              }}
            />
            <div className="flex flex-col gap-y-9">
              {stats.map((stat, i) => {
                const blockDelay = i * STAGGER_MS;
                return (
                  <div key={stat.label}>
                    <AnimatedStat value={stat.value} active={inView} delayMs={blockDelay} />
                    <div className="-mt-1">
                      <StatLabel
                        label={stat.label}
                        active={inView}
                        delayMs={blockDelay + MOBILE_INTRA_STAGGER_MS * 2}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tablet and up (sm+): the original row layout — three numbers,
              one continuous ruler drawing in left-to-right underneath all
              of them in a single smooth motion (same origin-left scaleX
              technique, and the same total duration, as the mobile rule
              above — timed to finish alongside the last number/label
              pair's own reveal), three labels. */}
          <div className="hidden sm:block">
            <div className="grid grid-cols-3 gap-x-6 md:gap-x-8">
              {stats.map((stat, i) => (
                <AnimatedStat
                  key={stat.label}
                  value={stat.value}
                  active={inView}
                  delayMs={i * STAGGER_MS}
                />
              ))}
            </div>

            <div
              className="mt-3.5 h-0.5 origin-left bg-accent transition-transform ease-engineered"
              style={{
                transform: `scaleX(${inView ? 1 : 0})`,
                transitionDuration: `${(stats.length - 1) * STAGGER_MS + 700}ms`,
              }}
            />

            <div className="mt-3.5 grid grid-cols-3 gap-x-6 md:gap-x-8">
              {stats.map((stat, i) => (
                <StatLabel
                  key={stat.label}
                  label={stat.label}
                  active={inView}
                  delayMs={i * STAGGER_MS}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
